import * as THREE from 'three';

export const PYRAMID_CAMERA_FOV = 12.3;
export const PYRAMID_CAMERA_DISTANCE = 19.65;
export const PYRAMID_CAMERA_Y = -0.445;
export const PYRAMID_EDGE_ROTATION = Math.PI / 4;

const PYRAMID_HEIGHT = 3.44;
const PYRAMID_WIDTH = 3.1;
const PYRAMID_TILT = Math.PI * 0.11;
const TIER_CUTS = [
  [0, 0.356],
  [0.417, 0.652],
  [0.711, 1],
] as const;
const EDGE_RADIUS = 0.025;
const EDGE_SEGMENTS = 8;
const TIER_EDGE_DEPTH = 0.011;
const TIER_EDGE_INSET = 0.008;
const TOP_BRIGHTNESS = 1.65;
const BOTTOM_BRIGHTNESS = 0.47;
const GRAIN_SIZE = 512;

interface PyramidMark {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>;
  textures: THREE.Texture[];
  environment: THREE.WebGLRenderTarget;
}

interface ProfilePoint {
  x: number;
  z: number;
  normalX: number;
  normalZ: number;
}

interface TierRing {
  cut: number;
  inset: number;
  normalBlend: number;
  brightnessScale: number;
}

function random(seed: number): [number, number] {
  const next = (seed * 1664525 + 1013904223) >>> 0;
  return [next, next / 0xffffffff];
}

function blurNoise(source: Float32Array, radius: number): Float32Array {
  const horizontal = new Float32Array(source.length);
  const result = new Float32Array(source.length);
  const diameter = radius * 2 + 1;

  for (let y = 0; y < GRAIN_SIZE; y += 1) {
    for (let x = 0; x < GRAIN_SIZE; x += 1) {
      let sum = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const sampleX = (x + offset + GRAIN_SIZE) % GRAIN_SIZE;
        sum += source[y * GRAIN_SIZE + sampleX];
      }
      horizontal[y * GRAIN_SIZE + x] = sum / diameter;
    }
  }

  for (let y = 0; y < GRAIN_SIZE; y += 1) {
    for (let x = 0; x < GRAIN_SIZE; x += 1) {
      let sum = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const sampleY = (y + offset + GRAIN_SIZE) % GRAIN_SIZE;
        sum += horizontal[sampleY * GRAIN_SIZE + x];
      }
      result[y * GRAIN_SIZE + x] = sum / diameter;
    }
  }

  return result;
}

function toByte(value: number): number {
  return Math.round(THREE.MathUtils.clamp(value, 0, 255));
}

function createGrainTextures(renderer: THREE.WebGLRenderer): THREE.Texture[] {
  const colorData = new Uint8Array(GRAIN_SIZE * GRAIN_SIZE * 4);
  const roughnessData = new Uint8Array(GRAIN_SIZE * GRAIN_SIZE * 4);
  const bumpData = new Uint8Array(GRAIN_SIZE * GRAIN_SIZE * 4);
  const sparkleData = new Uint8Array(GRAIN_SIZE * GRAIN_SIZE * 4);
  const noise = new Float32Array(GRAIN_SIZE * GRAIN_SIZE);
  const fleckNoise = new Float32Array(GRAIN_SIZE * GRAIN_SIZE);
  let seed = 0x50524d44;

  for (let i = 0; i < noise.length; i += 1) {
    [seed, noise[i]] = random(seed);
    [seed, fleckNoise[i]] = random(seed);
  }

  const softNoise = blurNoise(noise, 6);
  const clusteredFlecks = blurNoise(fleckNoise, 2);

  for (let i = 0; i < GRAIN_SIZE * GRAIN_SIZE; i += 1) {
    const fine = noise[i] - 0.5;
    const soft = softNoise[i] - 0.5;
    const fleck = Math.pow(
      THREE.MathUtils.clamp((clusteredFlecks[i] - 0.53) / 0.12, 0, 1),
      1.25,
    );
    const grain = toByte(
      THREE.MathUtils.clamp(234 + soft * 90 + fine * 4 + fleck * 42, 225, 255),
    );
    const roughness = toByte(205 + soft * 120 + fine * 10 - fleck * 145);
    const bump = toByte(128 + soft * 220 + fine * 24 + fleck * 48);
    const sparkle = toByte(fleck * 255);

    const offset = i * 4;
    colorData[offset] = grain;
    colorData[offset + 1] = grain;
    colorData[offset + 2] = grain;
    colorData[offset + 3] = 255;

    roughnessData[offset] = roughness;
    roughnessData[offset + 1] = roughness;
    roughnessData[offset + 2] = roughness;
    roughnessData[offset + 3] = 255;

    bumpData[offset] = bump;
    bumpData[offset + 1] = bump;
    bumpData[offset + 2] = bump;
    bumpData[offset + 3] = 255;

    sparkleData[offset] = sparkle;
    sparkleData[offset + 1] = sparkle;
    sparkleData[offset + 2] = sparkle;
    sparkleData[offset + 3] = 255;
  }

  const colorTexture = new THREE.DataTexture(
    colorData,
    GRAIN_SIZE,
    GRAIN_SIZE,
    THREE.RGBAFormat,
  );
  colorTexture.colorSpace = THREE.SRGBColorSpace;

  const roughnessTexture = new THREE.DataTexture(
    roughnessData,
    GRAIN_SIZE,
    GRAIN_SIZE,
    THREE.RGBAFormat,
  );
  const bumpTexture = new THREE.DataTexture(
    bumpData,
    GRAIN_SIZE,
    GRAIN_SIZE,
    THREE.RGBAFormat,
  );
  const sparkleTexture = new THREE.DataTexture(
    sparkleData,
    GRAIN_SIZE,
    GRAIN_SIZE,
    THREE.RGBAFormat,
  );

  for (const texture of [
    colorTexture,
    roughnessTexture,
    bumpTexture,
    sparkleTexture,
  ]) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1.25, 1.5);
    texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.needsUpdate = true;
  }

  return [colorTexture, roughnessTexture, bumpTexture, sparkleTexture];
}

function createEnvironment(renderer: THREE.WebGLRenderer): THREE.WebGLRenderTarget {
  const width = 64;
  const height = 32;
  const data = new Uint8Array(width * height * 4);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const u = x / (width - 1);
      const v = y / (height - 1);
      const key =
        Math.exp(-Math.pow((u - 0.34) / 0.11, 2)) *
        Math.exp(-Math.pow((v - 0.32) / 0.24, 2));
      const fill =
        Math.exp(-Math.pow((u - 0.72) / 0.17, 2)) *
        Math.exp(-Math.pow((v - 0.42) / 0.28, 2));
      const top = Math.exp(-Math.pow(v / 0.2, 2));
      const offset = (y * width + x) * 4;

      data[offset] = toByte(72 + key * 183 + fill * 112 + top * 76);
      data[offset + 1] = toByte(56 + key * 190 + fill * 104 + top * 72);
      data[offset + 2] = toByte(24 + key * 164 + fill * 82 + top * 58);
      data[offset + 3] = 255;
    }
  }

  const studio = new THREE.DataTexture(
    data,
    width,
    height,
    THREE.RGBAFormat,
  );
  studio.mapping = THREE.EquirectangularReflectionMapping;
  studio.colorSpace = THREE.SRGBColorSpace;
  studio.wrapS = THREE.RepeatWrapping;
  studio.needsUpdate = true;

  const generator = new THREE.PMREMGenerator(renderer);
  const environment = generator.fromEquirectangular(studio);
  studio.dispose();
  generator.dispose();
  return environment;
}

function createGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const uvs: number[] = [];
  const halfWidth = PYRAMID_WIDTH / 2;
  const cornerCenter = 1 - EDGE_RADIUS;
  const corners = [
    [cornerCenter, cornerCenter, Math.PI / 2, 0],
    [cornerCenter, -cornerCenter, 0, -Math.PI / 2],
    [-cornerCenter, -cornerCenter, -Math.PI / 2, -Math.PI],
    [-cornerCenter, cornerCenter, Math.PI, Math.PI / 2],
  ] as const;
  const profile: ProfilePoint[] = [];

  corners.forEach(([centerX, centerZ, startAngle, endAngle]) => {
    for (let segment = 0; segment <= EDGE_SEGMENTS; segment += 1) {
      const angle = THREE.MathUtils.lerp(startAngle, endAngle, segment / EDGE_SEGMENTS);
      const normalX = Math.cos(angle);
      const normalZ = Math.sin(angle);
      profile.push({
        x: centerX + EDGE_RADIUS * normalX,
        z: centerZ + EDGE_RADIUS * normalZ,
        normalX,
        normalZ,
      });
    }
  });

  const perimeter: number[] = [0];
  for (let point = 0; point < profile.length; point += 1) {
    const current = profile[point];
    const next = profile[(point + 1) % profile.length];
    perimeter.push(perimeter[point] + Math.hypot(next.x - current.x, next.z - current.z));
  }

  const addVertex = (
    x: number,
    y: number,
    z: number,
    normal: THREE.Vector3,
    brightness: number,
    u: number,
    v: number,
  ) => {
    positions.push(x, y, z);
    normals.push(normal.x, normal.y, normal.z);
    colors.push(brightness, brightness, brightness);
    uvs.push(u, v);
  };

  const createTierRings = (topCut: number, bottomCut: number): TierRing[] => {
    const depth = Math.min(TIER_EDGE_DEPTH, (bottomCut - topCut) / 4);
    const topEdgeBrightness = 1.8 + topCut * 3;
    const topTransitionBrightness = 1.35 + topCut * 1.1;
    const rings: TierRing[] =
      topCut === 0
        ? [{ cut: topCut, inset: 0, normalBlend: 0, brightnessScale: 1 }]
        : [
            {
              cut: topCut,
              inset: TIER_EDGE_INSET,
              normalBlend: 0.1,
              brightnessScale: topEdgeBrightness,
            },
            {
              cut: topCut + depth * 0.45,
              inset: TIER_EDGE_INSET * 0.28,
              normalBlend: 0.04,
              brightnessScale: topTransitionBrightness,
            },
            {
              cut: topCut + depth,
              inset: 0,
              normalBlend: 0,
              brightnessScale: 1,
            },
          ];

    rings.push(
      {
        cut: bottomCut - depth,
        inset: 0,
        normalBlend: 0,
        brightnessScale: 1,
      },
      {
        cut: bottomCut - depth * 0.45,
        inset: TIER_EDGE_INSET * 0.28,
        normalBlend: -0.05,
        brightnessScale: 1,
      },
      {
        cut: bottomCut,
        inset: TIER_EDGE_INSET,
        normalBlend: -0.12,
        brightnessScale: 1,
      },
    );
    return rings;
  };

  TIER_CUTS.forEach(([topCut, bottomCut]) => {
    const rings = createTierRings(topCut, bottomCut);

    for (let face = 0; face < profile.length; face += 1) {
      const current = profile[face];
      const next = profile[(face + 1) % profile.length];
      const currentSupport = current.x * current.normalX + current.z * current.normalZ;
      const nextSupport = next.x * next.normalX + next.z * next.normalZ;
      const currentNormal = new THREE.Vector3(
        PYRAMID_HEIGHT * current.normalX,
        halfWidth * currentSupport,
        PYRAMID_HEIGHT * current.normalZ,
      ).normalize();
      const nextNormal = new THREE.Vector3(
        PYRAMID_HEIGHT * next.normalX,
        halfWidth * nextSupport,
        PYRAMID_HEIGHT * next.normalZ,
      ).normalize();
      const uCurrent = perimeter[face] / 2;
      const uNext = perimeter[face + 1] / 2;

      for (let ring = 0; ring < rings.length - 1; ring += 1) {
        const upper = rings[ring];
        const lower = rings[ring + 1];
        const upperY = PYRAMID_HEIGHT / 2 - upper.cut * PYRAMID_HEIGHT;
        const lowerY = PYRAMID_HEIGHT / 2 - lower.cut * PYRAMID_HEIGHT;
        const upperRadius = Math.max(0, halfWidth * upper.cut - upper.inset);
        const lowerRadius = Math.max(0, halfWidth * lower.cut - lower.inset);
        const upperTargetY = Math.sign(upper.normalBlend);
        const lowerTargetY = Math.sign(lower.normalBlend);
        const upperCurrentNormal = currentNormal
          .clone()
          .lerp(
            new THREE.Vector3(0, upperTargetY, 0),
            Math.abs(upper.normalBlend),
          )
          .normalize();
        const upperNextNormal = nextNormal
          .clone()
          .lerp(new THREE.Vector3(0, upperTargetY, 0), Math.abs(upper.normalBlend))
          .normalize();
        const lowerCurrentNormal = currentNormal
          .clone()
          .lerp(
            new THREE.Vector3(0, lowerTargetY, 0),
            Math.abs(lower.normalBlend),
          )
          .normalize();
        const lowerNextNormal = nextNormal
          .clone()
          .lerp(new THREE.Vector3(0, lowerTargetY, 0), Math.abs(lower.normalBlend))
          .normalize();
        const upperBrightness =
          THREE.MathUtils.lerp(TOP_BRIGHTNESS, BOTTOM_BRIGHTNESS, upper.cut) *
          upper.brightnessScale;
        const lowerBrightness =
          THREE.MathUtils.lerp(TOP_BRIGHTNESS, BOTTOM_BRIGHTNESS, lower.cut) *
          lower.brightnessScale;
        const upperV = (bottomCut - upper.cut) / (bottomCut - topCut);
        const lowerV = (bottomCut - lower.cut) / (bottomCut - topCut);
        const upperLeft = [
          current.x * upperRadius,
          upperY,
          current.z * upperRadius,
        ] as const;
        const upperRight = [
          next.x * upperRadius,
          upperY,
          next.z * upperRadius,
        ] as const;
        const lowerLeft = [
          current.x * lowerRadius,
          lowerY,
          current.z * lowerRadius,
        ] as const;
        const lowerRight = [
          next.x * lowerRadius,
          lowerY,
          next.z * lowerRadius,
        ] as const;

        addVertex(
          ...upperLeft,
          upperCurrentNormal,
          upperBrightness,
          uCurrent,
          upperV,
        );
        addVertex(
          ...lowerLeft,
          lowerCurrentNormal,
          lowerBrightness,
          uCurrent,
          lowerV,
        );
        addVertex(
          ...lowerRight,
          lowerNextNormal,
          lowerBrightness,
          uNext,
          lowerV,
        );
        addVertex(
          ...upperLeft,
          upperCurrentNormal,
          upperBrightness,
          uCurrent,
          upperV,
        );
        addVertex(
          ...lowerRight,
          lowerNextNormal,
          lowerBrightness,
          uNext,
          lowerV,
        );
        addVertex(
          ...upperRight,
          upperNextNormal,
          upperBrightness,
          uNext,
          upperV,
        );
      }
    }
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.computeBoundingSphere();
  return geometry;
}

export function createPyramidMark(renderer: THREE.WebGLRenderer): PyramidMark {
  const textures = createGrainTextures(renderer);
  const environment = createEnvironment(renderer);
  const material = new THREE.MeshPhysicalMaterial({
    color: 0xd5a93f,
    map: textures[0],
    roughnessMap: textures[1],
    bumpMap: textures[2],
    bumpScale: 0.018,
    emissive: 0xffc95c,
    emissiveMap: textures[3],
    emissiveIntensity: 0.12,
    envMap: environment.texture,
    envMapIntensity: 0.72,
    metalness: 0.4,
    roughness: 0.72,
    clearcoat: 0.28,
    clearcoatMap: textures[3],
    clearcoatRoughness: 0.28,
    specularColor: 0xfff1c9,
    specularIntensity: 0.82,
    vertexColors: true,
    side: THREE.FrontSide,
  });
  const mesh = new THREE.Mesh(createGeometry(), material);
  mesh.rotation.x = PYRAMID_TILT;
  return { mesh, textures, environment };
}

export function addPyramidLighting(scene: THREE.Scene): void {
  scene.add(new THREE.AmbientLight(0xffdb91, 1.68));

  const keyLight = new THREE.DirectionalLight(0xffffdf, 4.05);
  keyLight.position.set(-5, 0, 5);
  scene.add(keyLight);
}

export function disposePyramidMark({
  mesh,
  textures,
  environment,
}: PyramidMark): void {
  mesh.geometry.dispose();
  mesh.material.dispose();
  textures.forEach((texture) => texture.dispose());
  environment.dispose();
}
